import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { Sparkle, Warning } from "@phosphor-icons/react";

const DIFFICULTIES = ["easy", "medium", "hard"];
const COUNTRIES = ["NG", "GH", "ALL"];

export default function Heatmap() {
  const [data, setData] = useState(null);
  const [country, setCountry] = useState("ALL");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/admin/questions/heatmap"); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const matrix = useMemo(() => {
    if (!data) return null;
    const out = {};
    data.buckets.filter((b) => b.country_tag === country).forEach((b) => {
      out[b.category] = out[b.category] || {};
      out[b.category][b.difficulty] = b;
    });
    return out;
  }, [data, country]);

  const fillWeak = async () => {
    if (busy || !data) return;
    if (data.weak.length === 0) { toast.success("No weak buckets — your bank is balanced 🎉"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/questions/ai/generate-now", { count: 20 });
      toast.success(`Steered AI run inserted ${res.inserted} questions targeting weak buckets`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-nk-mute">Loading…</p>;

  return (
    <div className="space-y-6">
      <Heading title="Topic Coverage Heatmap"
               subtitle={`Per-category × difficulty × country fresh-question counts. Anything below ${data.min_fresh} fresh is flagged as weak — the daily AI auto-seed loop will steer toward these buckets automatically.`} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {COUNTRIES.map((c) => (
            <button key={c} onClick={() => setCountry(c)} data-testid={`heatmap-country-${c}`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-sm border ${
                      country === c ? "bg-nk-accent text-nk-bg border-nk-accent" :
                      "text-nk-mute border-nk-line hover:border-nk-accent hover:text-nk-text"
                    }`}>{c === "ALL" ? "Global (ALL)" : c}</button>
          ))}
        </div>
        <button onClick={fillWeak} disabled={busy || data.weak.length === 0}
                data-testid="heatmap-fill-weak"
                className="px-3 py-1.5 bg-nk-warn text-nk-bg text-xs font-medium rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40">
          <Sparkle size={11} weight="fill" /> {busy ? "Working…" : `Generate ${20} now (steered to weak buckets)`}
        </button>
        <div className="ml-auto text-[11px] text-nk-mute">
          {data.total_buckets} buckets · <span className="text-nk-bad">{data.weak_buckets} weak</span> · {data.in_cooldown_count} in cooldown
        </div>
      </div>

      {/* Matrix */}
      <div className="nk-card p-4 overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="heatmap-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-nk-dim">
              <th className="py-2 pr-4 sticky left-0 bg-nk-bg">Category</th>
              {DIFFICULTIES.map((d) => <th key={d} className="py-2 px-2 text-center">{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {Object.keys(matrix || {}).sort().map((cat) => (
              <tr key={cat} className="border-t border-nk-line/40">
                <td className="py-2 pr-4 sticky left-0 bg-nk-bg text-nk-text">{cat}</td>
                {DIFFICULTIES.map((d) => {
                  const b = matrix[cat]?.[d];
                  if (!b) return <td key={d} className="py-2 px-2 text-center text-nk-dim font-mono">—</td>;
                  const cls = b.weak ? "text-nk-bad font-bold" : b.fresh < 25 ? "text-nk-warn" : "text-nk-accent";
                  return (
                    <td key={d} className="py-2 px-2 text-center font-mono">
                      <span className={cls}>{b.fresh}</span>
                      <span className="text-nk-dim text-[10px]"> / {b.total}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
            {Object.keys(matrix || {}).length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-nk-dim text-xs">No data for {country}.</td></tr>
            )}
          </tbody>
        </table>
        <p className="text-[10px] text-nk-dim mt-3">
          fresh / total · fresh = approved + not in 14-day cooldown
        </p>
      </div>

      {data.weak.length > 0 && (
        <div className="nk-card p-4 border-l-2 border-nk-bad">
          <p className="eyebrow text-nk-bad mb-2 flex items-center gap-1.5">
            <Warning size={11} weight="fill" /> Weak buckets ({data.weak.length})
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            {data.weak.slice(0, 20).map((b, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-2 py-1 border border-nk-line/40 rounded-sm">
                <span className="text-nk-text">{b.category}</span>
                <span className="text-nk-mute">{b.difficulty} · {b.country_tag}</span>
                <span className="font-mono text-nk-bad">{b.fresh} fresh</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
